import {
  ArrowRight,
  Calendar,
  Search,
  Plus,
  FileText,
  Globe,
  Type,
  BookOpen,
  MoreVertical,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

/**
 * Knowledge card type for the mockup
 */
interface KnowledgeCard {
  type: "text" | "file" | "website"
  title: string
  description: string
  metric: string
  metricLabel: string
}

const knowledgeCards: KnowledgeCard[] = [
  {
    type: "file",
    title: "Company Policies",
    description: "HR policies, procedures, and guidelines for employees...",
    metric: "12",
    metricLabel: "documents",
  },
  {
    type: "text",
    title: "Product FAQ",
    description: "Frequently asked questions about our products and services...",
    metric: "2,500",
    metricLabel: "words",
  },
  {
    type: "website",
    title: "Support Docs",
    description: "Technical documentation and support articles from help center...",
    metric: "25",
    metricLabel: "pages",
  },
]

/**
 * Returns the icon component for a knowledge type
 */
function getTypeIcon(type: KnowledgeCard["type"]) {
  switch (type) {
    case "file":
      return <FileText className="h-4 w-4" />
    case "website":
      return <Globe className="h-4 w-4" />
    case "text":
      return <Type className="h-4 w-4" />
  }
}

/**
 * Returns the color classes for a knowledge type
 */
function getTypeColor(type: KnowledgeCard["type"]) {
  switch (type) {
    case "file":
      return "bg-blue-500/20 text-blue-400"
    case "website":
      return "bg-purple-500/20 text-purple-400"
    case "text":
      return "bg-green-500/20 text-green-400"
  }
}

/**
 * Knowledge Base Dashboard Mockup component that visually represents the actual knowledge base interface.
 * This is a non-functional visual representation used for marketing purposes.
 */
function KnowledgeDashboardMockup() {
  return (
    <div className="w-full rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/20">
            <BookOpen className="h-4 w-4 text-primary" />
          </div>
          <span className="text-sm font-medium text-white">Knowledge Base</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Search Bar */}
          <div className="flex items-center gap-2 rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-1.5">
            <Search className="h-3.5 w-3.5 text-neutral-500" />
            <span className="text-xs text-neutral-500">Search knowledge...</span>
          </div>
          {/* Add Knowledge Button */}
          <div className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white">
            <Plus className="h-3.5 w-3.5" />
            Add Knowledge
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4">
        {/* Stats Row */}
        <div className="mb-4 flex gap-4">
          {[
            { label: "Total Items", value: "3", color: "text-white" },
            { label: "Documents", value: "12", color: "text-blue-400" },
            { label: "Words", value: "15.2K", color: "text-green-400" },
            { label: "Embeddings", value: "847", color: "text-purple-400" },
          ].map((stat, i) => (
            <div key={i} className="flex-1 rounded-lg border border-neutral-800 bg-neutral-800/50 p-3">
              <div className="text-xs text-neutral-500 mb-1">{stat.label}</div>
              <div className={`text-lg font-semibold ${stat.color}`}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Knowledge Cards Grid */}
        <div className="grid grid-cols-3 gap-3">
          {knowledgeCards.map((card, i) => (
            <div
              key={i}
              className="rounded-lg border border-neutral-700 bg-neutral-800 p-3 hover:border-neutral-600 transition-colors"
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-2">
                <div className={`rounded-lg p-2 ${getTypeColor(card.type)}`}>
                  {getTypeIcon(card.type)}
                </div>
                <MoreVertical className="h-4 w-4 text-neutral-500" />
              </div>

              {/* Card Title */}
              <h3 className="text-sm font-medium text-white mb-1">{card.title}</h3>

              {/* Card Description */}
              <p className="text-xs text-neutral-500 mb-3 line-clamp-2">{card.description}</p>

              {/* Card Footer */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-neutral-400">
                  {card.metric} {card.metricLabel}
                </span>
                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                  AI Enhanced
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Hero section for the Knowledge Base product page.
 * Features a dark gradient background with a mockup of the knowledge base dashboard.
 */
export function HeroSection() {
  const navigate = useNavigate()

  return (
    <section className="relative overflow-hidden bg-neutral-900 pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Text Content - Centered */}
        <div className="mb-12 text-center">
          <Badge className="mb-6 border-primary/30 bg-primary/10 text-primary">
            Knowledge Base
          </Badge>
          <h1 className="mb-6 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            Give Your AI Agents the{" "}
            <span className="text-primary">Knowledge They Need</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-neutral-400">
            Build rich knowledge bases from documents, websites, and text. Power your AI
            agents with contextual intelligence for smarter, more accurate responses.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="group bg-primary text-white hover:bg-primary/90"
              onClick={() => navigate("/signup")}
            >
              Start Building
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-neutral-700 bg-transparent text-white hover:bg-neutral-800"
              onClick={() => navigate("/demo")}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Book a Demo
            </Button>
          </div>
        </div>

        {/* Knowledge Dashboard Mockup */}
        <div className="relative">
          <KnowledgeDashboardMockup />
          {/* Decorative glow */}
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-r from-primary/20 via-transparent to-secondary/20 opacity-50 blur-2xl" />
        </div>
      </div>
    </section>
  )
}
